import { AxiosPromise } from 'axios'
import { AppApiResponseData } from '../types/api'
import { PatientDataRequest } from '../types/searchPatients'
import http from './http'
import {
	AssignPatientDataRequest,
	PatientDashboardData,
	PatientResponseFromApi,
	PatientsGetRequestBody,
	UnassignPatientDataRequest,
} from '../types/patients'
import { NoteForPatient } from '../types/noteForPatient'
import {
	Questionnaire,
	SendQuestionnaireMessageRequestData,
} from '../types/questionnaire'
import { TimelineItem } from '../types/timeline'
import { CreateReportRequestData } from '../types/reports'
import { Task } from '../types/task'
import { Timers } from '../types/common'
import { PatientDevicesInfo } from '../types/devices'
import { DataBulkActionRequest } from '../types/bulk'
import { CoachSystemData } from '../types/coachSystemData'

export function getListNameOfPatientsInOrganization(
	orgId: string,
	filterValue: string | number
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'GET',
		url: `/p/patient/organization/${orgId}?filter=${filterValue}`,
	})
}

export function getPatientDashboard(
	patientID: string
): AxiosPromise<AppApiResponseData<PatientDashboardData>> {
	return http({
		method: 'GET',
		url: `dario/${patientID}/patient/patientDashboard`,
	})
}

export function getPatientsList(
	data: PatientDataRequest
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `/p/patient/search`,
		data,
	})
}

export function getPatients(data: PatientsGetRequestBody): AxiosPromise<
	AppApiResponseData<{
		data: Array<PatientResponseFromApi>
		totalCount: number
	}>
> {
	return http({
		method: 'POST',
		url: `/p/patient/search`,
		data,
	})
}

export function plan(
	patientId: string
): AxiosPromise<AppApiResponseData<string>> {
	return http({
		method: 'GET',
		url: `/p/patient/${patientId}/plan`,
	})
}

export function notes(
	patientId: string
): AxiosPromise<AppApiResponseData<Array<NoteForPatient>>> {
	return http({
		method: 'GET',
		url: `/p/patient/${patientId}/notes`,
	})
}

export function questionnaires(
	patientId: string
): AxiosPromise<AppApiResponseData<Array<Questionnaire>>> {
	return http({
		method: 'GET',
		url: `/p/patient/${patientId}/questionnaires`,
	})
}

export function getPatientAccount(
	patient: string
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'GET',
		url: `/sf/account/${patient}`,
	})
}
export function getPatientAccountOrders(
	patient: string
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'GET',
		url: `/sf/account/${patient}/orders`,
	})
}

export function sendQuestionnaireMessage(
	patientId: string,
	data: SendQuestionnaireMessageRequestData
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `/p/patient/${patientId}/sendQuestionnaireMessage`,
		data,
	})
}

export function getChecklistTimeline(
	patientId: string,
	type: string,
	page: number,
	limit: number
): AxiosPromise<AppApiResponseData<Array<TimelineItem>>> {
	return http({
		method: 'GET',
		// eslint-disable-next-line max-len
		url: `/p/patient/${patientId}/timeline?type=${type}&page=${page}&limit=${limit}`,
	})
}

export function getPatientTimeLine(
	patientId: string,
	page: number,
	limit: number
): AxiosPromise<AppApiResponseData<Array<TimelineItem>>> {
	return http({
		method: 'GET',
		url: `p/patient/${patientId}/timeLine?page=${page}&limit=${limit}`,
	})
}

export function getTasks(
	patientID: string,
	status: string
): AxiosPromise<AppApiResponseData<Array<Task>>> {
	return http({
		method: 'GET',
		url: `/p/patient/${patientID}/tasks?status=${status}`,
	})
}

export function update(
	data: PatientDashboardData
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'PUT',
		url: '/p/patient',
		data,
	})
}

export function updateCoachSystemData(
	data: Partial<CoachSystemData>
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'PUT',
		url: '/p/patient',
		data,
	})
}

export function createReportRequest(
	patientId: string,
	data: CreateReportRequestData
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `/dario/${patientId}/report`,
		data,
	})
}

export function deleteReport(
	patientId: string,
	reportId: string
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'DELETE',
		url: `/dario/${patientId}/report/${reportId}`,
	})
}

export function downloadReport(
	patientId: string,
	reportId: string
): Promise<any> {
	const headers = new Headers()
	headers.set('x-access-token', localStorage.getItem('access_token') || '')
	return fetch(
		`${process.env.REACT_APP_URL}/api/v2/dario/${patientId}/report/download/${reportId}`,
		{
			headers,
		}
	)
}

export function reports(
	patientId: string
): AxiosPromise<AppApiResponseData<Array<any>>> {
	return http({
		method: 'GET',
		url: `/dario/${patientId}/reports`,
	})
}

export function sendSms(
	patientID: string,
	phone: string,
	text: string
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `/p/patient/${patientID}/sendSms`,
		data: {
			phone,
			text,
		},
	})
}

export function saveTimer(
	patientID: string,
	start: string,
	end: string,
	type: Timers
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `/p/patient/${patientID}/timer`,
		data: {
			timer: {
				type,
				start,
				end,
			},
		},
	})
}

export function devices(
	patientID: string
): AxiosPromise<AppApiResponseData<PatientDevicesInfo>> {
	return http({
		method: 'GET',
		url: `/p/patient/${patientID}/devices`,
	})
}

export function setMeeting(
	patientID: string
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `/p/patient/${patientID}/setmeeting`,
	})
}

export function pushMessage(
	patientID: string,
	alert: string
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `/p/patient/${patientID}/sendPushMessage`,
		data: {
			alert,
		},
	})
}

export function pushBatchBulkAction(
	data: any
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `p/patients/batch`,
		data,
	})
}

export function executeBatchBulkAction(
	data: DataBulkActionRequest
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `p/patients/batch`,
		data,
	})
}

export function assignPatient(
	data: AssignPatientDataRequest
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: '/p/coach/assign/patients',
		data,
	})
}

export function unassignPatient(
	data: UnassignPatientDataRequest
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: '/p/coach/remove/patients',
		data,
	})
}

export function removePatient(
	ID: string,
	data: {
		ordersToCancel: Array<string>
	}
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `p/patient/${ID}/remove`,
		data,
	})
}

export function importPatient(data: any): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `p/import/patient`,
		data,
	})
}


export function sendEducationalArticle(
	ID: string,
	data: any
): AxiosPromise<AppApiResponseData> {
	return http({
		method: 'POST',
		url: `/p/patient/${ID}/sendEducationalMessage`,
		data,
	})
}
